import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { FormBuilder, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import {
  ContractConsortiumService,
  Consortium,
  ConsortiumMember,
  ConsortiumMemberPayload,
} from 'src/app/services/contract-consortium.service';
import { ContractedCompanyService } from 'src/app/services/contracted-company.service';
import { ContractedCompany } from 'src/models/contracted-company';
import { ToastService } from 'src/app/services/toast.service';
import { formatCnpj } from 'src/app/helpers/cnpj.helper';

export type ConsortiumMemberDraft = ConsortiumMemberPayload & {
  id: string;
  contractedCompany?: ContractedCompany;
};

type ConsortiumMemberLike = ConsortiumMember | ConsortiumMemberDraft;

@Component({
  selector: 'app-contract-consortium',
  templateUrl: './contract-consortium.component.html',
  styleUrls: ['./contract-consortium.component.css'],
})
export class ContractConsortiumComponent implements OnInit, OnChanges {
  @Input() contractId?: string;
  @Input() isConsortiumMode = false;
  @Output() isConsortiumModeChange = new EventEmitter<boolean>();
  @Input() consortiumName = '';
  @Output() consortiumNameChange = new EventEmitter<string>();
  @Input() allowDraftMode = false;
  @Input() initialConsortiumId = '';

  @Input() draft: ConsortiumMemberDraft[] = [];
  @Output() draftChange = new EventEmitter<ConsortiumMemberDraft[]>();

  @Input() showToggle: boolean = true;

  @ViewChild('confirmRemoveTpl') confirmRemoveTpl!: TemplateRef<any>;

  loading = false;
  members: ConsortiumMemberLike[] = [];
  contractedCompanies: ContractedCompany[] = [];
  consortiums: Consortium[] = [];
  selectedConsortiumId = '';
  contractedCompaniesLoading = false;
  consortiumsLoading = false;

  formOpen = false;
  editingId?: string;

  modalRef?: BsModalRef;
  memberPendingDelete?: ConsortiumMemberLike;

  // Erros
  duplicateError = false;
  leaderError = false;

  // Ver mais / Ver menos
  showAll = false;
  initialDisplayCount = 4;

  form = this.fb.group({
    consortiumName: ['', [Validators.required]],
    contractedCompanyId: ['', [Validators.required]],
    isLeader: [false],
    participationPercentage: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
  });

  constructor(
    private fb: FormBuilder,
    private consortiumService: ContractConsortiumService,
    private contractedCompanyService: ContractedCompanyService,
    private modalService: BsModalService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.loadContractedCompanies();
    this.loadConsortiums();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contractId']) {
      this.selectedConsortiumId = '';
      this.load();
    }

    if (changes['initialConsortiumId'] && !this.contractId) {
      const nextConsortiumId = (changes['initialConsortiumId'].currentValue || '').trim();
      if (nextConsortiumId) {
        this.selectedConsortiumId = nextConsortiumId;
        this.syncSelectedConsortiumName();
        this.load();
      }
    }

    if (changes['draft'] && this.isDraftMode) {
      this.members = [...(this.draft ?? [])];
    }

    if (changes['isConsortiumMode']) {
      if (!this.isConsortiumMode) {
        this.members = [];
        this.formOpen = false;
        this.emitDraft();
      }
    }

    if (
      changes['isConsortiumMode'] &&
      changes['isConsortiumMode'].currentValue === true &&
      !changes['isConsortiumMode'].previousValue &&
      this.members.length === 0
    ) {
      this.openCreate();
    }
  }

  get isDraftMode(): boolean {
    return this.allowDraftMode && !this.contractId && !this.selectedConsortiumId;
  }

  get title(): string {
    return 'Empresas do Consórcio';
  }

  get currentConsortiumId(): string | undefined {
    if (this.contractId) return undefined;
    return this.selectedConsortiumId || undefined;
  }

  get canSaveConsortiumName(): boolean {
    return Boolean((this.consortiumName || '').trim());
  }

  get f() {
    return this.form.controls;
  }

  get displayedMembers(): ConsortiumMemberLike[] {
    if (this.showAll || this.members.length <= this.initialDisplayCount) {
      return this.members;
    }
    return this.members.slice(0, this.initialDisplayCount);
  }

  get totalPercentage(): number {
    return this.members.reduce((sum, m) => sum + (Number(m.participationPercentage) || 0), 0);
  }

  get hasLeader(): boolean {
    return this.members.some((m) => m.isLeader);
  }

  get leaderCompany(): ContractedCompany | undefined {
    const leader = this.members.find((m) => m.isLeader);
    return leader?.contractedCompany;
  }

  get isPercentageValid(): boolean {
    if (this.members.length === 0) return true;
    return Math.abs(this.totalPercentage - 100) < 0.01;
  }

  toggleConsortiumMode(): void {
    this.isConsortiumMode = !this.isConsortiumMode;
    this.isConsortiumModeChange.emit(this.isConsortiumMode);

    if (!this.isConsortiumMode) {
      this.members = [];
      this.formOpen = false;
      this.emitDraft();
    }
  }

  toggleShowAll(): void {
    this.showAll = !this.showAll;
  }

  isInvalid(name: keyof typeof this.form.controls): boolean {
    const c = this.form.get(name as string);
    return !!c && c.invalid && (c.touched || c.dirty);
  }

  errorMessage(name: keyof typeof this.form.controls): string {
    const c = this.form.get(name as string);
    if (!c || !c.errors) return '';

    if (c.errors['required']) return 'Campo obrigatório.';
    if (c.errors['min']) return `O valor mínimo é ${c.errors['min'].min}.`;
    if (c.errors['max']) return `O valor máximo é ${c.errors['max'].max}.`;

    return 'Valor inválido.';
  }

  private checkDuplicate(): boolean {
    const companyId = this.form.value.contractedCompanyId;
    if (!companyId) return false;

    return this.members.some((m) => {
      if (this.editingId && m.id === this.editingId) {
        return false;
      }
      return m.contractedCompanyId === companyId;
    });
  }

  private checkLeaderConflict(): boolean {
    if (!this.form.value.isLeader) return false;

    return this.members.some((m) => {
      if (this.editingId && m.id === this.editingId) {
        return false;
      }
      return m.isLeader;
    });
  }

  private loadContractedCompanies(): void {
    this.contractedCompaniesLoading = true;

    this.contractedCompanyService
      .getContractedCompanies({ limit: 1000, offset: 0 })
      .pipe(finalize(() => (this.contractedCompaniesLoading = false)))
      .subscribe({
        next: (list: ContractedCompany[]) => {
          this.contractedCompanies = Array.isArray(list) ? list : [];
        },
        error: (err) => {
          console.error(err);
          this.toastService.showError('Não foi possível carregar as empresas contratadas.');
        },
      });
  }

  private load(): void {
    if (this.isDraftMode) {
      this.loading = false;
      this.members = [...(this.draft ?? [])];
      if (this.consortiumName) {
        this.form.patchValue({ consortiumName: this.consortiumName });
      }

      if (this.members.length) {
        this.isConsortiumMode = true;
        this.isConsortiumModeChange.emit(true);
      }

      return;
    }

    if (this.contractId) {
      this.loading = true;
      this.consortiumService.getConsortiumDetails(this.contractId).subscribe({
        next: (details) => {
          this.members = (details?.members || []) as ConsortiumMemberLike[];

          const nextName = details?.consortium?.name || this.consortiumName || '';
          if (nextName) {
            this.consortiumName = nextName;
            this.consortiumNameChange.emit(nextName);
            this.form.patchValue({ consortiumName: nextName });
          }

          if (this.members.length) {
            this.isConsortiumMode = true;
            this.isConsortiumModeChange.emit(true);
          }

          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.members = [];
        },
      });
      return;
    }

    if (!this.currentConsortiumId) {
      this.loading = false;
      this.members = [];
      return;
    }

    this.loading = true;

    this.consortiumService.listConsortiumMembersById(this.currentConsortiumId).subscribe({
      next: (members) => {
        this.members = (members || []) as ConsortiumMemberLike[];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  openCreate(): void {
    this.editingId = undefined;
    this.duplicateError = false;
    this.leaderError = false;
    this.form.reset({
      consortiumName: this.consortiumName || '',
      contractedCompanyId: '',
      isLeader: false,
      participationPercentage: 0,
    });
    this.formOpen = true;
  }

  openEdit(member: ConsortiumMemberLike): void {
    this.editingId = member.id;
    this.duplicateError = false;
    this.leaderError = false;

    this.form.patchValue({
      consortiumName: this.consortiumName || '',
      contractedCompanyId: member.contractedCompanyId,
      isLeader: member.isLeader,
      participationPercentage: Number(member.participationPercentage) || 0,
    });

    this.formOpen = true;
  }

  cancel(): void {
    this.formOpen = false;
    this.editingId = undefined;
    this.duplicateError = false;
    this.leaderError = false;
  }

  clearForm(): void {
    this.duplicateError = false;
    this.leaderError = false;
    this.form.reset({
      consortiumName: this.consortiumName || '',
      contractedCompanyId: '',
      isLeader: false,
      participationPercentage: 0,
    });
    this.form.markAsUntouched();
    this.form.markAsPristine();
  }

  onCompanyChange(): void {
    this.duplicateError = this.checkDuplicate();
  }

  onLeaderChange(): void {
    this.leaderError = this.checkLeaderConflict();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.checkDuplicate()) {
      this.duplicateError = true;
      return;
    }

    if (this.checkLeaderConflict()) {
      this.leaderError = true;
      return;
    }

    this.duplicateError = false;
    this.leaderError = false;

    const consortiumName = (this.form.value.consortiumName || this.consortiumName || '').trim();
    if (!consortiumName) {
      this.toastService.showError('Informe o nome do consórcio.');
      return;
    }
    this.consortiumName = consortiumName;
    this.consortiumNameChange.emit(consortiumName);

    const company = this.contractedCompanies.find(
      (c) => c.id === this.form.value.contractedCompanyId
    );

    const payload: ConsortiumMemberPayload = {
      contractedCompanyId: this.form.value.contractedCompanyId!,
      isLeader: this.form.value.isLeader ?? false,
      participationPercentage: Number(this.form.value.participationPercentage) || 0,
    };

    if (this.isDraftMode) {
      const id = this.editingId ?? this.newDraftId();
      const nextMember: ConsortiumMemberDraft = {
        id,
        ...payload,
        contractedCompany: company,
      };

      const next = this.editingId
        ? this.members.map((m) => (m.id === this.editingId ? nextMember : m))
        : [...this.members, nextMember];

      this.members = next;
      this.formOpen = false;
      this.editingId = undefined;
      this.emitDraft();
      return;
    }

    if (this.contractId) {
      this.loading = true;

      const req$ = this.editingId
        ? this.consortiumService.updateMember(this.contractId, this.editingId, payload)
        : this.consortiumService.addMember(this.contractId, payload);

      req$.subscribe({
        next: () => {
          this.loading = false;
          this.formOpen = false;
          this.editingId = undefined;
          this.load();
          this.toastService.showSuccess('Empresa salva no consórcio do contrato.');
        },
        error: (err) => {
          this.loading = false;
          const msg = err?.error?.message || 'Erro ao salvar empresa no consórcio do contrato.';
          this.toastService.showError(msg);
        },
      });
      return;
    }

    if (!this.selectedConsortiumId) {
      this.toastService.showError('Salve o consórcio antes de adicionar empresas.');
      return;
    }

    this.loading = true;

    const req$ = this.editingId
      ? this.consortiumService.updateConsortiumMember(this.selectedConsortiumId, this.editingId, payload)
      : this.consortiumService.addConsortiumMember(this.selectedConsortiumId, payload);

    req$.subscribe({
      next: () => {
        this.loading = false;
        this.formOpen = false;
        this.editingId = undefined;
        this.load();
        this.toastService.showSuccess('Empresa salva no consórcio.');
      },
      error: (err) => {
        this.loading = false;
        const msg = err?.error?.message || 'Erro ao salvar empresa no consórcio.';
        this.toastService.showError(msg);
      },
    });
  }

  saveConsortium(): void {
    if (this.contractId) return;

    const consortiumName = (this.consortiumName || this.form.value.consortiumName || '').trim();
    if (!consortiumName) {
      this.form.get('consortiumName')?.markAsTouched();
      this.toastService.showError('Informe o nome do consórcio.');
      return;
    }

    this.loading = true;

    const req$ = this.selectedConsortiumId
      ? this.consortiumService.updateConsortium(this.selectedConsortiumId, consortiumName)
      : this.consortiumService.createConsortium(consortiumName);

    req$.subscribe({
      next: (consortium) => {
        this.loading = false;
        this.selectedConsortiumId = consortium.id;
        this.consortiumName = consortium.name;
        this.consortiumNameChange.emit(this.consortiumName);
        this.form.patchValue({ consortiumName: consortium.name });
        this.formOpen = false;
        this.loadConsortiums();
        this.load();
        this.toastService.showSuccess('Nome do consórcio salvo com sucesso.');
      },
      error: (err) => {
        this.loading = false;
        const msg = err?.error?.message || 'Erro ao salvar nome do consórcio.';
        this.toastService.showError(msg);
      },
    });
  }

  openRemoveModal(member: ConsortiumMemberLike): void {
    this.memberPendingDelete = member;
    this.modalRef = this.modalService.show(this.confirmRemoveTpl, {
      class: 'modal-dialog-centered',
      backdrop: 'static',
      keyboard: false,
    });
  }

  closeRemoveModal(): void {
    this.modalRef?.hide();
    this.modalRef = undefined;
    this.memberPendingDelete = undefined;
  }

  confirmRemove(): void {
    if (!this.memberPendingDelete) return;

    if (this.isDraftMode) {
      const id = this.memberPendingDelete.id;
      this.members = this.members.filter((m) => m.id !== id);
      this.closeRemoveModal();
      this.emitDraft();
      return;
    }

    this.loading = true;

    const req$ = this.contractId
      ? this.consortiumService.removeMember(this.contractId, this.memberPendingDelete.id)
      : this.consortiumService.removeConsortiumMember(this.selectedConsortiumId, this.memberPendingDelete.id);

    req$.subscribe({
      next: () => {
        this.loading = false;
        this.closeRemoveModal();
        this.load();
        this.toastService.showSuccess('Empresa removida com sucesso.');
      },
      error: () => {
        this.loading = false;
        this.toastService.showError('Erro ao remover empresa.');
      },
    });
  }

  trackById(_: number, item: ConsortiumMemberLike): string {
    return item.id;
  }

  displayCompanyName(member: ConsortiumMemberLike): string {
    const company = member.contractedCompany;
    if (!company) return 'Empresa não encontrada';
    return company.tradeName || company.corporateName || 'Sem nome';
  }

  displayCompanyCnpj(member: ConsortiumMemberLike): string {
    const company = member.contractedCompany;
    if (!company?.cnpj) return '';
    return formatCnpj(String(company.cnpj));
  }

  displayContractedCompany(company: ContractedCompany): string {
    const name = company?.tradeName || company?.corporateName || 'Empresa sem nome';
    const cnpj = company?.cnpj ? ` (${formatCnpj(String(company.cnpj))})` : '';
    return `${name}${cnpj}`;
  }

  private emitDraft(): void {
    this.draftChange.emit([...(this.members as ConsortiumMemberDraft[])]);
  }

  private newDraftId(): string {
    try {
      if (crypto?.randomUUID) return crypto.randomUUID();
    } catch { }
    return `draft_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  startNewConsortium(): void {
    if (this.contractId) return;

    this.selectedConsortiumId = '';
    this.members = [];
    this.consortiumName = '';
    this.consortiumNameChange.emit(this.consortiumName);
    this.formOpen = false;
    this.editingId = undefined;
    this.duplicateError = false;
    this.leaderError = false;
    this.form.reset({
      consortiumName: '',
      contractedCompanyId: '',
      isLeader: false,
      participationPercentage: 0,
    });
  }

  onConsortiumNameChange(value: string): void {
    this.consortiumName = value || '';
    this.consortiumNameChange.emit(this.consortiumName);
    this.form.patchValue({ consortiumName: this.consortiumName }, { emitEvent: false });
  }

  onConsortiumChange(consortiumId: string): void {
    this.selectedConsortiumId = consortiumId || '';
    this.syncSelectedConsortiumName();
    this.members = [];
    this.formOpen = false;
    this.load();
  }

  displayConsortium(consortium: Consortium): string {
    return consortium?.name || 'Consórcio sem nome';
  }

  private loadConsortiums(): void {
    this.consortiumsLoading = true;
    this.consortiumService
      .listConsortiums()
      .pipe(finalize(() => (this.consortiumsLoading = false)))
      .subscribe({
        next: (list) => {
          this.consortiums = Array.isArray(list) ? list : [];
          this.syncSelectedConsortiumName();
        },
        error: () => {
          this.toastService.showError('Não foi possível carregar consórcios.');
        },
      });
  }

  private syncSelectedConsortiumName(): void {
    const selected = this.consortiums.find(c => c.id === this.selectedConsortiumId);
    this.consortiumName = selected?.name || this.consortiumName || '';
    this.consortiumNameChange.emit(this.consortiumName);
    this.form.patchValue({ consortiumName: this.consortiumName || '' }, { emitEvent: false });
  }
}
